import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet"; // SEO için eklendi
import { ChevronLeft, ChevronRight, Search, MapPin, Heart, X } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Leaflet İkon Düzeltmesi
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

const ankaraBgUrl = "https://images.unsplash.com/photo-1610214357759-4293570b218f?auto=format&fit=crop&w=1600&q=80";
const ITEMS_PER_PAGE = 9;
const API_URL = process.env.REACT_APP_API_URL;

// --- Harita Modal Bileşeni (Tasarım İyileştirildi) ---
const MapModal = ({ mekanlar, onClose }) => {
  const position = [39.9255, 32.8596]; // Ankara Merkez

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm transition-opacity duration-300"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-[80vh] bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[1000] p-2 bg-gray-800/90 hover:bg-red-500 text-white rounded-full transition-colors shadow-lg border border-gray-600"
          aria-label="Kapat"
        >
          <X size={24} />
        </button>
        
        <MapContainer center={position} zoom={12} className="h-full w-full z-0">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          />
          {mekanlar.map((mekan, index) =>
            mekan.latitude && mekan.longitude && (
              <Marker key={mekan.id || index} position={[mekan.latitude, mekan.longitude]}>
                <Popup>
                  <div className="text-center">
                    <strong className="block text-indigo-700 text-sm mb-1">{mekan.ad}</strong>
                    <span className="text-xs text-gray-600">{mekan.ilce}</span>
                  </div>
                </Popup>
              </Marker>
            )
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default function AnkaraPageSingle() {
  const navigate = useNavigate();
  
  // State
  const [ilceFilter, setIlceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [mekanlar, setMekanlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMapModal, setShowMapModal] = useState(false);

  // Veri Çekme
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ankara-mekanlari`);
        if (!response.ok) throw new Error("Veri sunucudan alınamadı.");
        const data = await response.json();
        // Veriyi her zaman array formatına zorla
        const normalized = Array.isArray(data) ? data : [data];
        setMekanlar(normalized);
      } catch (err) {
        setError("Mekanlar yüklenirken bir sorun oluştu.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // İlçe Listesi Oluşturma (Memoized)
  const ilceler = useMemo(() => {
    const ilceSet = new Set(mekanlar.map((m) => m.ilce).filter(Boolean));
    return Array.from(ilceSet).sort((a, b) => a.localeCompare(b, "tr"));
  }, [mekanlar]);

  // Filtreleme Mantığı (Memoized)
  const filteredMekanlar = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return mekanlar.filter((m) => {
      const ilceUyar = ilceFilter ? m.ilce === ilceFilter : true;
      const adUyar = m.ad?.toLowerCase().includes(lowerSearch);
      // Backend'den gelen veri yapısına göre description alanı değişebilir, kontrol edelim:
      const aciklamaMetni = m.bilgi || m.aciklama_bolumleri?.[0]?.icerik || "";
      const bilgiUyar = aciklamaMetni.toLowerCase().includes(lowerSearch);
      
      return ilceUyar && (adUyar || bilgiUyar);
    });
  }, [ilceFilter, search, mekanlar]);

  // Sayfalama Mantığı
  const totalPages = Math.ceil(filteredMekanlar.length / ITEMS_PER_PAGE);
  const paginatedMekanlar = filteredMekanlar.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" }); // Sayfa başına kaydır
    }
  };

  // Filtre değişince sayfa 1'e dön
  useEffect(() => {
    setCurrentPage(1);
  }, [ilceFilter, search]);

  return (
    <main className="min-h-screen bg-gray-900 text-gray-50 font-inter">
      {/* SEO Optimizasyonu */}
      <Helmet>
        <title>Ankara Mekan Rehberi | Tarihi ve Kültürel Gezi</title>
        <meta name="description" content="Ankara'nın tarihi kaleleri, müzeleri, camileri ve kültürel mekanlarını keşfedin. İlçe ilçe gezi rehberi ve harita konumu." />
        <meta name="keywords" content="ankara gezilecek yerler, ankara müzeleri, anıtkabir, ankara kalesi, başkent gezi" />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <header 
        className="relative bg-cover bg-center py-24 md:py-32"
        style={{ backgroundImage: `url(${ankaraBgUrl})` }}
      >
        <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-[2px]" /> {/* Karartma */}
        
        <div className="relative max-w-7xl mx-auto px-4 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-blue-500 to-indigo-600 mb-6 drop-shadow-2xl">
            Ankara'yı Keşfet
          </h1>
          <p className="mt-4 text-xl text-gray-200 font-light max-w-3xl mx-auto leading-relaxed">
            Kalelerden müzelere, <span className="text-teal-400 font-medium">geçmişin izini</span> bugünün kalbinde sür. Başkentin saklı hazinelerini keşfetmeye başla.
          </p>

          {/* Arama ve Filtre Alanı */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-gray-800/60 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-gray-700/50 flex flex-col md:flex-row gap-4">
              
              {/* İlçe Seçimi */}
              <div className="relative w-full md:w-1/3">
                <select
                  className="w-full px-5 py-4 text-gray-200 bg-gray-900/80 rounded-2xl border border-gray-600 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/50 outline-none transition-all appearance-none cursor-pointer"
                  value={ilceFilter}
                  onChange={(e) => setIlceFilter(e.target.value)}
                  aria-label="İlçe Filtrele"
                >
                  <option value="">Tüm İlçeler</option>
                  {ilceler.map((ilce) => (
                    <option key={ilce} value={ilce}>{ilce}</option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </div>
              </div>

              {/* Arama Input */}
              <div className="relative flex-grow">
                <span className="absolute inset-y-0 left-0 flex items-center pl-5 pointer-events-none">
                  <Search className="w-5 h-5 text-teal-400" />
                </span>
                <input
                  type="text"
                  placeholder="Mekan, müze veya anahtar kelime..."
                  className="w-full pl-12 pr-5 py-4 text-gray-200 bg-gray-900/80 rounded-2xl border border-gray-600 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/50 outline-none transition-all placeholder-gray-500"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Aksiyon Butonları */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <button
                onClick={() => setShowMapModal(true)}
                className="flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold shadow-lg shadow-indigo-500/30 transition-all transform hover:-translate-y-1"
              >
                <MapPin className="h-5 w-5 mr-2" /> Haritada Göster
              </button>
              
              <button 
                onClick={() => navigate(`/favorite-mekan`)} 
                className="flex items-center px-6 py-3 rounded-full bg-gray-800 hover:bg-gray-700 text-white font-semibold border border-gray-600 hover:border-gray-500 shadow-lg transition-all transform hover:-translate-y-1"
              >
                <Heart className="h-5 w-5 mr-2 text-rose-500 fill-current" /> Favorilerim
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* --- İÇERİK ALANI --- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        
        {/* Yükleniyor Durumu */}
        {loading && (
          <div className="flex flex-col justify-center items-center py-20">
             <div className="w-16 h-16 border-4 border-t-4 border-teal-500 border-gray-700 rounded-full animate-spin"></div>
             <p className="mt-4 text-gray-400 animate-pulse">Mekanlar yükleniyor...</p>
          </div>
        )}

        {/* Hata Durumu */}
        {!loading && error && (
          <div className="text-center py-20 bg-red-900/20 rounded-3xl border border-red-900/50">
            <p className="text-xl text-red-400 font-semibold">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-sm text-gray-300 underline hover:text-white">Sayfayı Yenile</button>
          </div>
        )}

        {/* Boş Sonuç Durumu */}
        {!loading && !error && filteredMekanlar.length === 0 && (
          <div className="text-center py-24 bg-gray-800/30 rounded-3xl border border-gray-700 border-dashed">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-2xl text-gray-400">Aradığınız kriterlere uygun mekan bulunamadı.</p>
            <p className="text-gray-500 mt-2">Farklı bir arama terimi veya ilçe deneyebilirsiniz.</p>
          </div>
        )}

        {/* Kart Listesi */}
        {!loading && !error && filteredMekanlar.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {paginatedMekanlar.map((mekan, index) => (
              <article
                key={mekan.id || index}
                className="group relative bg-gray-800 rounded-3xl overflow-hidden border border-gray-700 shadow-lg hover:shadow-2xl hover:shadow-teal-500/10 transition-all duration-500 hover:-translate-y-2 flex flex-col h-full"
              >
                {/* Görsel Alanı */}
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={mekan.gorsel || "https://placehold.co/600x400/1f2937/14b8a6?text=Görsel+Yok"}
                    alt={mekan.ad}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://placehold.co/600x400/1f2937/14b8a6?text=Görsel+Yok";
                    }}
                  />
                  {/* İlçe Rozeti */}
                  {mekan.ilce && (
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md text-teal-300 text-xs font-bold px-3 py-1 rounded-full border border-teal-500/30 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {mekan.ilce}
                    </div>
                  )}
                </div>

                {/* İçerik */}
                <div className="p-6 flex flex-col flex-grow relative z-10 bg-gray-800">
                  <h2 className="text-2xl font-bold text-white mb-3 leading-snug group-hover:text-teal-400 transition-colors line-clamp-1">
                    {mekan.ad}
                  </h2>
                  
                  <p className="text-gray-400 text-sm mb-6 line-clamp-3 leading-relaxed flex-grow">
                    {mekan.bilgi || mekan.aciklama_bolumleri?.[0]?.icerik || "Bu mekan için henüz detaylı açıklama eklenmemiş."}
                  </p>

                  {/* Buton */}
                  <div className="mt-auto pt-4 border-t border-gray-700">
                    <button
                      onClick={() => navigate(`/mekan/${mekan.id || index}`)}
                      className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center group/btn"
                    >
                      Detayları İncele 
                      <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Sayfalama */}
        {!loading && !error && totalPages > 1 && (
          <nav className="flex justify-center items-center mt-16 space-x-2" aria-label="Sayfalama">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                currentPage === 1
                  ? "text-gray-600 bg-gray-800 cursor-not-allowed"
                  : "text-white bg-gray-800 hover:bg-gray-700 hover:text-teal-400 border border-gray-700"
              }`}
            >
              <ChevronLeft className="h-5 w-5" /> Önceki
            </button>
            
            <div className="hidden md:flex items-center px-4 py-3 bg-gray-800 rounded-xl border border-gray-700 text-gray-300 font-medium">
              Sayfa <span className="text-teal-400 font-bold mx-2">{currentPage}</span> / {totalPages}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                currentPage === totalPages
                  ? "text-gray-600 bg-gray-800 cursor-not-allowed"
                  : "text-white bg-gray-800 hover:bg-gray-700 hover:text-teal-400 border border-gray-700"
              }`}
            >
              Sonraki <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        )}
      </section>

      {/* Harita Modalı */}
      {showMapModal && (
        <MapModal mekanlar={mekanlar} onClose={() => setShowMapModal(false)} />
      )}
    </main>
  );
}